<!-- https://www.figma.com/file/JGnLzMLGla9gSsvnW00NUY/Burning-Design-System-%F0%9F%94%A5?node-id=743%3A67432&t=rk5MTxanM2aanWJT-0 -->
<template>
  <button
    class="reaction-button"
    :class="buttonClasses"
    :aria-label="`Поставить реакцию на статью ${articleTitle}`"
    @click="$emit('reaction-button-click')"
    @touchstart.prevent.stop="touchStart"
    @touchend.prevent.stop="touchEnd"
  >
    <span
      class="reaction-button__icon"
      :class="iconClasses"
      @animationend="handleAnimationEnd"
    />
    <span
      v-show="isShowCount"
      class="reaction-button__count"
    >
      {{ count }}
    </span>
  </button>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { AvailableReaction } from '~/types/components/Reactions';

  const LONG_TAP_DELAY = 400;

  export default defineNuxtComponent({
    name: 'ReactionButton',

    props: {
      articleTitle: {
        type: String as PropType<string>,
        default: '',
      },

      hideCount: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      count: {
        type: Number as PropType<number>,
        default: 0,
      },

      buttonReaction: {
        type: String as PropType<AvailableReaction>,
        default: 'default',
      },

      buttonType: {
        type: String as PropType<'menu' | 'button'>,
        default: 'button',
      },

      isCurrent: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      isMenuOpened: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      showZeroCount: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
    },

    data: () => ({
      animateIcon: false,
      isLongTap: false,
      longTapTimeout: null as null | NodeJS.Timeout,
    }),

    computed: {
      buttonClasses(): Record<string, boolean> {
        return {
          [`reaction-button--${this.buttonReaction}`]: true,
          'reaction-button--opened-menu': this.isMenuOpened,
          current: !!this.isCurrent,
        };
      },

      iconClasses(): Record<string, boolean> {
        return {
          [`reaction-button__icon--${this.buttonType}`]: true,
          'reaction-button__icon--animation': this.animateIcon,
        };
      },

      isShowCount(): boolean {
        return !this.hideCount && (this.showZeroCount || !!this.count);
      },
    },

    watch: {
      buttonReaction(newValue: AvailableReaction): void {
        this.animateIcon = false;

        setTimeout(() => {
          this.animateIcon = newValue !== 'default';
        }, 0);
      },
    },

    methods: {
      handleAnimationEnd() {
        this.animateIcon = false;
      },

      touchStart(): void {
        this.isLongTap = false;
        this.longTapTimeout = setTimeout(() => {
          this.$emit('reaction-button-hold');
          this.isLongTap = true;
        }, LONG_TAP_DELAY);
      },

      touchEnd() {
        if (this.longTapTimeout) {
          clearTimeout(this.longTapTimeout);
        }

        !this.isLongTap && this.$emit('reaction-button-click');
      },
    },
  });
</script>

<style lang="scss" scoped>
  $button-icon-size: 24px;
  $menu-icon-size: 40px;
  $icon-class: reaction-button__icon;
  $current-class: current;

  .reaction-button {
    @include smallText;
    font-weight: 500;

    gap: 2px;
    padding: $spacing-1 $spacing-3;
    color: $text-color--1;
    border-radius: 40px;
    cursor: pointer;
    display: flex;
    align-items: center;

    &--heart {
      &.#{$current-class} {
        background: #df32e21a;
      }

      .#{$icon-class} {
        @include heartIcon;
      }
    }

    &--thumbs_up {
      &.#{$current-class} {
        background: #49ea3b1a;
      }

      .#{$icon-class} {
        @include thumbsUpIcon;
      }
    }

    &--on_fire {
      &.#{$current-class} {
        background: #ed55001a;
      }

      .#{$icon-class} {
        @include fireIcon;
      }
    }

    &--cry {
      &.#{$current-class} {
        background: #699bdc1a;
      }

      .#{$icon-class} {
        @include cryIcon;
      }
    }

    &--angry {
      &.#{$current-class} {
        background: #a16bff1a;
      }

      .#{$icon-class} {
        @include angryIcon;
      }
    }

    &--default {
      background: #f5f5f4;

      &.reaction-button--opened-menu {
        background: $hover-color--5;
      }

      .#{$icon-class} {
        @include noReaction('%234C4C4C');
      }
    }

    &--simple {
      background: none;

      .#{$icon-class} {
        @include noReaction('%234C4C4C');
        @include hover {
          @include noReaction('%23000000');
        }
      }
    }

    @include hover {
      .reaction-button__icon--menu {
        transform: scale(1.15);
        transform-origin: center center;
      }
    }

    & > &__icon {
      &--button {
        width: $button-icon-size;
        height: $button-icon-size;
        background-size: $button-icon-size;
      }

      &--menu {
        width: $menu-icon-size;
        height: $menu-icon-size;
        background-size: $menu-icon-size;
        transition: transform 0.3s;
      }

      &--animation {
        animation: zoom-in-zoom-out 1s ease 2;
      }
    }

    @keyframes zoom-in-zoom-out {
      0% {
        transform: scale(1, 1);
      }

      50% {
        transform: scale(1.16, 1.16);
      }

      100% {
        transform: scale(1, 1);
      }
    }
  }
</style>
